import { Check, MessageCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink } from "@/lib/whatsapp";

const SERVICES = [
    {
        id: "cardapio-pizzarias",
        tag: "Restaurantes, Pizzarias & Docerias",
        title: "Cardápio Digital para o Seu Negócio",
        description:
            "Pare de pagar até 27% de comissão para apps de entrega. Tenha sua própria plataforma de pedidos integrada ao WhatsApp — ideal para restaurantes, pizzarias, docerias, lanchonetes e qualquer comércio de alimentação.",
        bullets: [
            "Sistema próprio com link personalizado",
            "Painel simples para editar produtos e preços",
            "Pedido completo enviado direto no WhatsApp",
            "Zero taxa sobre vendas — você paga apenas pelo desenvolvimento",
        ],
        cta: "Quero Cardápio sem Taxas",
        intent: "cardapio",
        badge: "Zero Comissões",
        featured: true,
        image: "https://images.unsplash.com/photo-1622880833523-7cf1c0bd4296?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxkZWxpY2lvdXMlMjBwaXp6YSUyMG92ZW4lMjBzbGljZXxlbnwwfHx8fDE3OTAyOTY0Mzl8MA&ixlib=rb-4.1.0&q=85",
        alt: "Prato artesanal saindo do forno — cardápio digital para restaurantes e pizzarias",
    },
    {
        id: "sites-landingpages",
        tag: "Presença Digital & Vendas",
        title: "Sites & Landing Pages",
        description:
            "Seu negócio em destaque na internet com sites ultrarrápidos, modernos e desenhados para atrair clientes do seu bairro.",
        bullets: [
            "Páginas 100% focadas em conversão e leads",
            "Responsivo para celular, tablet e computador",
            "Otimização para buscas locais no Google",
            "Botões diretos para WhatsApp e ligações",
        ],
        cta: "Quero Criar Meu Site",
        intent: "sites",
        badge: "Mais Procurado",
        featured: true,
        image: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHx0ZWNoJTIwaGFyZHdhcmUlMjBjb21wdXRlciUyMGNvZGV8ZW58MHx8fHwxNzkwMjk2NDM4fDA&ixlib=rb-4.1.0&q=85",
        alt: "Notebook com código na tela — desenvolvimento de sites",
    },
    {
        id: "suporte-informatica",
        tag: "Infraestrutura & Manutenção",
        title: "Suporte de TI & Informática",
        description:
            "Recupere o desempenho total dos seus computadores e garanta a estabilidade da sua operação diária.",
        bullets: [
            "Formatação limpa e instalação segura de Windows",
            "Remoção completa de vírus, malwares e lentidão",
            "Seu computador mais rápido e otimizado no dia a dia",
            "Limpeza de arquivos inúteis e programas desnecessários",
        ],
        cta: "Pedir Suporte Técnico",
        intent: "suporte",
        badge: "Atendimento no Local",
        featured: false,
        image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHx0ZWNoJTIwaGFyZHdhcmUlMjBjb21wdXRlciUyMGNvZGV8ZW58MHx8fHwxNzkwMjk2NDM4fDA&ixlib=rb-4.1.0&q=85",
        alt: "Placa de circuito com iluminação azul — manutenção de computadores",
    },
];

export const Services = () => (
    <section id="servicos" data-testid="services-section" className="relative py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal className="max-w-2xl">
                <span className="text-xs font-bold uppercase tracking-[0.24em] text-cyan-400">
                    Nossos Serviços
                </span>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                    Serviços especializados para o seu negócio
                </h2>
                <p className="mt-4 text-base text-slate-400 sm:text-lg">
                    Atendimento rápido, confiável e sem intermediários — direto no
                    Campo Limpo.
                </p>
            </Reveal>

            <div className="mt-14 grid gap-6 md:grid-cols-3">
                {SERVICES.map((s, i) => (
                    <Reveal key={s.id} delay={i * 0.12}>
                        <article
                            data-testid={`service-card-${s.id}`}
                            className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-[#111827] transition-all duration-300 hover:-translate-y-2 hover:border-cyan-400/40 hover:shadow-[0_16px_48px_rgba(0,136,255,0.14)] ${
                                s.featured ? "border-cyan-400/25" : "border-white/[0.07]"
                            }`}
                        >
                            {s.featured && (
                                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent" aria-hidden="true" />
                            )}
                            <div className="relative h-44 overflow-hidden">
                                <img
                                    src={s.image}
                                    alt={s.alt}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-[#111827]/30 to-transparent" />
                                <span
                                    data-testid={`service-badge-${s.id}`}
                                    className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-100 backdrop-blur-md"
                                >
                                    {s.badge}
                                </span>
                            </div>
                            <div className="flex flex-1 flex-col p-6 pt-2">
                                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400/90">
                                    {s.tag}
                                </span>
                                <h3 className="mt-2 text-xl font-bold tracking-tight text-slate-50">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                    {s.description}
                                </p>
                                <ul className="mt-5 space-y-2.5">
                                    {s.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                                            <Check size={15} className="mt-0.5 shrink-0 text-[#25D366]" strokeWidth={3} />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href={waLink(s.intent)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`service-cta-${s.id}`}
                                    className="mt-7 inline-flex items-center justify-center gap-2 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-5 py-3 text-sm font-bold text-[#25D366] transition-all duration-300 hover:bg-[#25D366] hover:text-[#052e12]"
                                >
                                    <MessageCircle size={16} strokeWidth={2.5} />
                                    {s.cta}
                                </a>
                                {s.intent === "cardapio" && (
                                    <a
                                        href="/demo"
                                        data-testid="service-demo-link"
                                        className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/[0.08] px-5 py-2.5 text-xs font-bold text-cyan-300 transition-all duration-300 hover:bg-cyan-400 hover:text-[#03202e]"
                                    >
                                        Ver demonstração ao vivo →
                                    </a>
                                )}
                            </div>
                        </article>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
